import React, { useCallback, useMemo } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import useVocabularyStore from '../../store/vocabulary-store';
import useCrudListPage from '../../shared/hooks/useCrudListPage';
import DataTable from '../../shared/components/table/DataTable';
import PageHeader from '../../shared/components/table/PageHeader';
import SearchInput from '../../shared/components/table/SearchInput';
import RowActions from '../../shared/components/table/RowActions';
import ResourceFormModal from '../../shared/components/modal/ResourceFormModal';
import DeleteModal from '../../shared/components/modal/DeleteModal';

const columnHelper = createColumnHelper();

const vocabularyFields = [
  { name: 'find', label: 'Find', type: 'text', required: true },
  { name: 'replace', label: 'Replace', type: 'text', required: true },
];

const VocabularyPage = () => {
  const { vocabulary, fetchVocabulary, createVocabulary, updateVocabulary, deleteVocabulary } =
    useVocabularyStore();

  const openEditRef = React.useRef(() => {});
  const openDeleteRef = React.useRef(() => {});

  const columns = useMemo(
    () => [
      columnHelper.accessor('id', { header: 'ID' }),
      columnHelper.accessor('find', { header: 'Find' }),
      columnHelper.accessor('replace', { header: 'Replace' }),
      columnHelper.accessor('actions', {
        header: 'Actions',
        cell: ({ row }) => (
          <RowActions
            onEdit={() => openEditRef.current(row.original)}
            onDelete={() => openDeleteRef.current(row.original)}
          />
        ),
      }),
    ],
    []
  );

  const sortItems = useCallback(
    (items) => [...items].sort((a, b) => a.find.localeCompare(b.find)),
    []
  );

  const {
    table,
    isLoading,
    globalFilter,
    setGlobalFilter,
    isFormOpen,
    formMode,
    isDeleteModalOpen,
    selectedItem,
    deletePayload,
    openCreate,
    openEdit,
    openDelete,
    closeModals,
    handleFormSubmit,
    handleDelete,
  } = useCrudListPage({
    items: vocabulary,
    fetchItems: fetchVocabulary,
    createItem: createVocabulary,
    updateItem: updateVocabulary,
    deleteItem: deleteVocabulary,
    columns,
    sortItems,
    entityLabel: 'Vocabulary',
    getDeletePayload: (item) => ({ ...item, dataName: 'vocabulary' }),
  });

  openEditRef.current = openEdit;
  openDeleteRef.current = openDelete;

  const initialValues = useMemo(
    () =>
      formMode === 'edit' && selectedItem
        ? { find: selectedItem.find || '', replace: selectedItem.replace || '' }
        : { find: '', replace: '' },
    [formMode, selectedItem]
  );

  return (
    <div className="max-w-screen-3xl mx-auto mt-4">
      <PageHeader title="Vocabulary List" onCreate={openCreate} />
      <div className="mb-4">
        <SearchInput value={globalFilter} onChange={setGlobalFilter} />
      </div>
      <DataTable
        table={table}
        columnsCount={columns.length}
        isLoading={isLoading}
        emptyMessage="No vocabulary found"
      />
      <ResourceFormModal
        isOpen={isFormOpen}
        onClose={closeModals}
        mode={formMode}
        title={formMode === 'edit' ? 'Edit Vocabulary' : 'Create Vocabulary'}
        fields={vocabularyFields}
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
      />
      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeModals}
        data={deletePayload}
        onDelete={handleDelete}
      />
    </div>
  );
};

export default VocabularyPage;
